import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useMediaUrl } from "@/lib/storageUrl";
import { BadgeCheck } from "lucide-react";
import { artGradient, hueFromId } from "@/lib/showcaseData";

export interface AuthorLike {
  id: string;
  name: string;
  handle?: string | null;
  avatarUrl?: string | null;
  verified?: boolean;
  meta?: string | null;
}

interface AuthorChipProps {
  author: AuthorLike;
  size?: "sm" | "md" | "lg";
  onClick?: () => void;
  right?: React.ReactNode;
}

const sizes = {
  sm: { av: "w-8 h-8", name: "text-xs", sub: "text-[10px]" },
  md: { av: "w-10 h-10", name: "text-sm", sub: "text-xs" },
  lg: { av: "w-14 h-14", name: "text-base", sub: "text-xs" },
};

/** Avatar + name + handle row — the profile-picture treatment used across every card. */
const AuthorChip = ({ author, size = "md", onClick, right }: AuthorChipProps) => {
  const s = sizes[size];
  const avatarUrl = useMediaUrl(author.avatarUrl);
  const initials = (author.name || author.handle || "?").trim().charAt(0).toUpperCase();
  const Wrapper = onClick ? "button" : "div";
  return (
    <Wrapper
      {...(onClick ? { type: "button" as const, onClick } : {})}
      className="flex items-center gap-2.5 min-w-0 w-full text-left"
    >
      <Avatar className={`${s.av} ring-2 ring-white/10 shrink-0`}>
        <AvatarImage src={avatarUrl ?? undefined} alt={author.name} />
        <AvatarFallback className="text-white font-semibold" style={{ backgroundImage: artGradient(hueFromId(author.id)) }}>
          {initials}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <p className={`${s.name} font-medium text-white truncate flex items-center gap-1`}>
          {author.name}
          {author.verified && <BadgeCheck className="w-3.5 h-3.5 text-sky-400 shrink-0" />}
        </p>
        <p className={`${s.sub} text-gray-500 truncate`}>
          {author.handle ? `@${author.handle}` : ""}{author.handle && author.meta ? " • " : ""}{author.meta ?? ""}
        </p>
      </div>
      {right}
    </Wrapper>
  );
};

export default AuthorChip;
